import { zodResolver } from '@hookform/resolvers/zod';
import { FinancialServiceProviderAdapters } from '@seapoint/server/enums';
import { useForm } from 'react-hook-form';
import z from 'zod';
import { Button } from '../../../../design-system/button/button';
import { Form } from '../../../../design-system/form/form-primitives';
import { Inline } from '../../../../design-system/inline/inline';
import { Stack } from '../../../../design-system/stack/stack';
import { AccountView } from '../../../../types/entity-types';
import { BankAccountMapping } from '../../integrations-page-wrapper';
import { AccountMappingTable, XeroBankAccountView } from './account-mapping-table';

const getAccountMappingFormSchema = (accounts: AccountView[]) =>
  z.object({
    accounts: z.array(
      z
        .object({
          accountId: z.string(),
          xeroAccountId: z.string(),
          fromDate: z.string(),
        })
        .superRefine(({ accountId, fromDate }, ctx) => {
          const account = accounts.find((a) => a.id === accountId);

          if (!account || !account.fspAdapter) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Account not found among the accounts to map',
              path: ['fromDate'],
            });

            return;
          }

          const { fspAdapter } = account;

          if (fspAdapter === FinancialServiceProviderAdapters.MODULR && !fromDate) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Date is required',
              path: ['fromDate'],
            });

            return;
          }
        })
        .superRefine(({ xeroAccountId }, ctx) => {
          if (!xeroAccountId) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Xero account ID is required',
              path: ['xeroAccountId'],
            });
          }
        }),
    ),
  });

export type AccountMappingFormValues = z.infer<ReturnType<typeof getAccountMappingFormSchema>>;

export type ExternalBankAccount = {
  id: string;
  name: string;
  currency: string;
};

type AccountMappingFormProps = {
  accounts: AccountView[];
  xeroBankAccounts: XeroBankAccountView[];
  xeroAccountMappings: BankAccountMapping[];
  isLoading: boolean;
  isCreatingXeroAccount: boolean;
  onCreateXeroAccount: (seapointAccountId: string) => Promise<void>;
  onSubmit: (values: AccountMappingFormValues['accounts']) => void;
};

export const AccountMappingForm = ({
  accounts,
  xeroBankAccounts,
  xeroAccountMappings,
  isLoading,
  isCreatingXeroAccount,
  onCreateXeroAccount,
  onSubmit,
}: AccountMappingFormProps) => {
  const form = useForm<AccountMappingFormValues>({
    resolver: zodResolver(getAccountMappingFormSchema(accounts)),
    defaultValues: {
      accounts: accounts.map((account) => {
        const xeroAccountMapping = xeroAccountMappings.find((mapping) => mapping.seapointAccountId === account.id);

        return {
          accountId: account.id,
          xeroAccountId: xeroAccountMapping?.externalAccountId || '',
          fromDate: '',
        };
      }),
    },
    mode: 'onTouched',
  });

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(({ accounts }) => {
          onSubmit(accounts);
        })}
      >
        <Stack>
          <AccountMappingTable
            accounts={accounts}
            xeroBankAccounts={xeroBankAccounts}
            isCreatingXeroAccount={isCreatingXeroAccount}
            onCreateXeroAccount={onCreateXeroAccount}
          />

          <Inline justify="end" className="w-full">
            <Button type="submit" variant="default" isLoading={isLoading} disabled={!form.formState.isValid}>
              Continue
            </Button>
          </Inline>
        </Stack>
      </form>
    </Form>
  );
};
